import { useEffect, useState } from "react";
import { getArticles } from "../api/strapi";
import "../styles/RecentPosts.css";

const fallbackArticles = [
  {
    id: 1,
    title: 'Integrating AI Into Product Design Workflows as a UX Engineer',
    slug: 'integrating-ai-into-product-design-workflows-as-a-ux-engineer',
    description: 'Exploring practical methods for integrating generative AI tools into rapid prototyping and component architecture.',
    publishedAt: new Date().toISOString(),
    tags: ['AI UX', 'Workflow', 'Prototyping']
  },
  {
    id: 2,
    title: 'UX Design in a World of AI: Why Empathy and Trust Matter More Than Ever',
    slug: 'ux-design-in-a-world-of-ai-why-empathy-and-trust-matter-more-than-ever',
    description: 'Why human-centered research and ethical design patterns are critical as algorithmic interfaces scale.',
    publishedAt: new Date().toISOString(),
    tags: ['UX Design', 'AI', 'Ethics']
  },
  {
    id: 3,
    title: 'What Traditional Art Taught Me About Designing Better Digital Products',
    slug: 'what-traditional-art-taught-me-about-designing-better-digital-products',
    description: 'Translating classical composition techniques, spatial balance, and color harmony into digital interfaces.',
    publishedAt: new Date().toISOString(),
    tags: ['Design Theory', 'Visual Design']
  }
];

export default function Blog() {
    const [articles, setArticles] = useState([]);
    const [error, setError] = useState(null);

    useEffect(() => {
        getArticles()
            .then((data) => {
                if (data && data.length > 0) {
                    setArticles(data);
                } else {
                    setArticles(fallbackArticles);
                }
            })
            .catch((err) => {
                console.warn("Strapi fetch failed, using fallback articles:", err);
                setArticles(fallbackArticles);
            });
    }, []);

    const formatDate = (dateString) => {
        if (!dateString) return '';
        const options = { year: 'numeric', month: 'long', day: 'numeric' };
        return new Date(dateString).toLocaleDateString(undefined, options);
    };

    const getFallbackExcerpt = (article) => {
        if (article.description) return article.description;
        if (!article.blocks || article.blocks.length === 0) return "No description available.";

        const richTextBlock = article.blocks.find(b => b.__component === "shared.rich-text");
        if (richTextBlock && richTextBlock.body) {
            const strippedText = richTextBlock.body.replace(/<[^>]+>/g, '');
            const firstSentenceMatch = strippedText.match(/[^.!?]+[.!?]+/);
            if (firstSentenceMatch) return firstSentenceMatch[0].trim();
            return strippedText.substring(0, 100).trim() + "...";
        }
        return "No description available.";
    };

    return (
        <div style={{ backgroundColor: "#fbf8f4", minHeight: "100vh" }}>
            <div className="container" style={{ paddingTop: "140px", paddingBottom: "100px" }}>
                {/* Site-wide Standard Header Block */}
                <div className="recent-thoughts-page-header text-start mb-5">
                    <h1 className="recent-thoughts-page-title mb-3">Recent Thoughts</h1>
                    <p className="recent-thoughts-page-subtitle">
                        Designing and shipping enterprise products at scale—backed by measurable impact.
                    </p>
                </div>

                {error && <p style={{ color: "red" }}>{error}</p>}

                <div className="row g-4 d-flex">
                    {articles.map((article, idx) => {
                        const bgColors = ['#eaf4f8', '#fde8ef', '#eaf6ec', '#fff1e6', '#f4eaf8', '#e6f5f3'];
                        const cardBg = bgColors[idx % bgColors.length];

                        return (
                            <div key={article.id} className="col-12 col-md-6 col-lg-4">
                                <div 
                                  className="card h-100 border-0 d-flex flex-column" 
                                  style={{ 
                                    backgroundColor: cardBg,
                                    borderRadius: "20px", 
                                    overflow: "hidden",
                                    boxShadow: "0 10px 28px rgba(0,0,0,0.03)",
                                    transition: "transform 0.25s ease, box-shadow 0.25s ease" 
                                  }} 
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.transform = 'translateY(-4px)';
                                    e.currentTarget.style.boxShadow = '0 16px 36px rgba(0,0,0,0.08)';
                                  }} 
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.transform = 'translateY(0)';
                                    e.currentTarget.style.boxShadow = '0 10px 28px rgba(0,0,0,0.03)';
                                  }}
                                >
                                    <div className="card-body d-flex flex-column p-4 p-md-5">
                                        <div className="d-flex align-items-center justify-content-between mb-3">
                                            <span className="badge bg-white text-dark border px-3 py-2 rounded-pill fw-bold" style={{ fontSize: "0.75rem", letterSpacing: "0.05em" }}>ARTICLE</span>
                                            {article.publishedAt && (
                                                <small className="text-muted fw-semibold">{formatDate(article.publishedAt)}</small>
                                            )}
                                        </div>

                                        <h3 className="card-title text-dark fw-extrabold mb-3" style={{ fontSize: "1.3rem", lineHeight: "1.35", letterSpacing: "-0.01em" }}>
                                          {article.title}
                                        </h3>

                                        {article.tags && article.tags.length > 0 && (
                                            <div className="mb-3 d-flex flex-wrap gap-1">
                                                {article.tags.map(tag => (
                                                    <span key={tag} className="badge bg-light text-dark border me-1">{tag}</span>
                                                ))}
                                            </div>
                                        )}

                                        <p className="card-text text-secondary mb-4 flex-grow-1" style={{ fontSize: "0.95rem", lineHeight: "1.6" }}>
                                          {getFallbackExcerpt(article)}
                                        </p>
                                        
                                        <div className="mt-auto pt-2">
                                            <a href={`/recent-thoughts/${article.slug}`} className="stretched-link text-decoration-none fw-bold text-dark d-inline-flex align-items-center gap-2">
                                                <span>Read Article</span>
                                                <span style={{ transition: "transform 0.2s ease" }}>→</span>
                                            </a>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}